import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { AppShell } from './AppShell'

// Added 2026-09-23: an org can no longer reach the rest of the app
// without completing real Paystack payment verification -- reverses the
// earlier "soft paywall" design (RegisterBilling.tsx's old "Skip for
// now" link) per direct user instruction. The policy itself
// (must_complete_billing) is computed server-side -- see auth_schemas.py's
// UserResponse docstring for why (false in simulated/dev mode, false
// once an org has ever verified even if it later lapses). These are the
// only paths that get an org through the checkout step itself, so they
// stay reachable regardless -- everything else redirects to
// /register/billing.
const BILLING_SETUP_PATHS = ['/register/business-setup', '/register/billing', '/billing/callback']

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, isImpersonating, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading…</div>
  }
  if (!user) {
    return <Navigate to="/login" replace />
  }
  // Impersonating admins and system admins are exempt -- a support
  // session or an internal admin account has no business being forced
  // through a customer's own checkout flow.
  if (
    user.must_complete_billing &&
    !isImpersonating &&
    !user.is_system_admin &&
    !BILLING_SETUP_PATHS.includes(location.pathname)
  ) {
    // Staff cannot pay: show them who can instead of the checkout screen.
    if (user.org_role && user.org_role !== 'owner') {
      return (
        <AppShell>
          <p className="max-w-lg text-sm text-foreground" data-testid="billing-not-finished">
            Billing setup is not finished. Ask your account owner to complete it.
          </p>
        </AppShell>
      )
    }
    return <Navigate to="/register/billing" replace />
  }
  return <AppShell>{children}</AppShell>
}

export function AdminRoute({ children }: { children: ReactNode }) {
  const { user, isImpersonating, isLoading } = useAuth()

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading…</div>
  }
  if (!user) {
    return <Navigate to="/login" replace />
  }
  // Mirrors the backend's require_system_admin: a request carrying an
  // impersonation token 403s there regardless of the target's own
  // is_system_admin, so this route must reject it the same way even if
  // the impersonated user happens to be an admin.
  if (!user.is_system_admin || isImpersonating) {
    return <Navigate to="/dashboard" replace />
  }
  return <AppShell>{children}</AppShell>
}
